import type { AnyFieldApi } from '@tanstack/react-form';
import type {
  ContentTypeDefinition,
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
  RelationWithoutHydration,
} from '../../../lib/flotiq-client/types/api-response';
import type { ContentTypeFormProperty } from '../helpers/parseProperty';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../../lib/helpers';

import Tooltip from '../../../components/Tooltip/Tooltip';

import ContentTypeFormContext from '../../../contexts/ContentTypeFormContext';
import ListPropertiesContext from '../../../contexts/ListPropertiesContext';
import PropertyModalCtdsContext from '../../../contexts/PropertyModalCtdsContext';

import { QuestionMarkCircleIcon } from '../../../images/shapes';

import { withForm } from '../../../form-hooks/form';
import { MIN_PROPERTY, PROPERTIES_FIELDS } from '../helpers/propertiesFields';

const BLOCK_TOOLS = {
  list: 'List',
  header: 'Header',
  image: 'Image',
  youtubeEmbed: 'YoutubeEmbed',
  quote: 'Quote',
  warning: 'Warning',
  delimiter: 'Delimiter',
  code: 'Code',
  table: 'Table',
};

const VALIDATION = {
  unique: { key: 'config.unique' as const, label: 'Unique' },
  required: { key: 'required' as const, label: 'Required' },
  relationMultiple: {
    key: 'config.validation.relationMultiple' as const,
    label: 'Multiple',
  },
  showTime: { key: 'config.showTime' as const, label: 'ShowTimePicker' },
  useOptionsWithLabels: {
    key: 'config.useOptionsWithLabels' as const,
    label: 'LabelOptions',
  },
  multiple: { key: 'config.multiple' as const, label: 'Multiple' },
};

const ADDITIONAL = {
  isPassword: { key: 'config.isPassword' as const, label: 'Password' },
  readonly: { key: 'config.readonly' as const, label: 'Readonly' },
  hidden: { key: 'config.hidden' as const, label: 'Hidden' },
  isTitlePart: { key: 'config.isTitlePart' as const, label: 'PartOfTitle' },
};

const PropertiesSettings = withForm({
  defaultValues: {} as ContentTypeFormProperty,
  props: {
    inputType: '',
    isMedia: false,
    testId: '',
  },
  render: function Render({ form, inputType, isMedia, testId }) {
    const { t } = useTranslation();
    const { setDirty } = use(ContentTypeFormContext);
    const { ctds, ctdsAreLoading, filterCtds, getValidationType } = use(
      PropertyModalCtdsContext,
    );
    const { isNestedList } = use(ListPropertiesContext);

    const [extraValidationType, setExtraValidationType] =
      useState<ContentTypeDefinition | null>(null);

    const relationContentType = useStore(
      form.store,
      ({ values }) =>
        (values as ContentTypeFormProperty).config?.validation
          ?.relationContenttype,
    );

    useEffect(() => {
      if (!relationContentType) setExtraValidationType(null);
      else
        getValidationType?.(relationContentType).then((ctd) =>
          setExtraValidationType(ctd),
        );
    }, [getValidationType, relationContentType]);

    const defaultFieldListeners = useMemo(
      () => ({
        onChange: () => {
          setDirty(true);
        },
      }),
      [setDirty],
    );

    const handleRequiredFieldChange = useCallback(
      (
        value: boolean,
        minProperty: { key: string; value?: unknown },
        fieldApi: AnyFieldApi,
      ) => {
        setDirty(true);
        if (!minProperty?.key) return;
        const isRequired = value;
        if (isRequired) {
          fieldApi.form.setFieldValue(
            `schema.${minProperty.key}`,
            minProperty.value || 1,
          );
        } else {
          fieldApi.form.setFieldValue(`schema.${minProperty.key}`, undefined);
        }
      },
      [setDirty],
    );

    const blockEditorTypesListeners = useMemo(
      () => ({
        onChange: ({
          value,
          fieldApi,
        }: {
          value: string[];
          fieldApi: AnyFieldApi;
        }) => {
          if (Array.isArray(value) && !value.length) {
            fieldApi.form.setFieldValue('config.blockEditorTypes', null);
          }
          setDirty(true);
        },
      }),
      [setDirty],
    );

    const handleDateTimeChange = useCallback(
      (showTime: boolean, fieldApi: AnyFieldApi) => {
        const defaultValue = fieldApi.form.getFieldValue('schema.default');

        if (defaultValue) {
          if (!showTime) {
            fieldApi.form.setFieldValue(
              'schema.default',
              defaultValue.split('T')[0],
            );
          } else {
            fieldApi.form.setFieldValue(
              'schema.default',
              defaultValue + 'T00:00',
            );
          }
        }

        setDirty(true);
      },
      [setDirty],
    );

    const handleUseLabelsChange = useCallback(
      (showLabels: boolean, fieldApi: AnyFieldApi) => {
        if (showLabels) {
          const newOptions = (
            (fieldApi.form.getFieldValue('config.options') as string[]) || []
          ).map((value) => ({
            value,
            label: value,
          }));

          fieldApi.form.setFieldValue('config.optionsWithLabels', newOptions);
          fieldApi.form.setFieldValue('config.options', undefined);
        } else {
          const newOptions = (
            (fieldApi.form.getFieldValue('config.optionsWithLabels') as {
              value: string;
              label: string;
            }[]) || []
          ).map((option) => option.value);
          fieldApi.form.setFieldValue('config.options', newOptions);
          fieldApi.form.setFieldValue('config.optionsWithLabels', undefined);
        }

        setDirty(true);
      },
      [setDirty],
    );

    const handleMultipleChange = useCallback(
      (isMultiple: boolean, fieldApi: AnyFieldApi) => {
        fieldApi.form.setFieldValue(
          'schema.type',
          isMultiple ? 'array' : 'string',
        );

        const defaultValue = fieldApi.form.getFieldValue('schema.default');

        if (isMultiple) {
          fieldApi.form.setFieldValue('schema.items', { type: 'string' });

          if (defaultValue) {
            fieldApi.form.setFieldValue('schema.default', [defaultValue]);
          }
        } else {
          fieldApi.form.setFieldValue('schema.items', undefined);

          if (defaultValue?.[0]) {
            const newDefaultValue = defaultValue[0];
            fieldApi.form.setFieldValue('schema.default', newDefaultValue);
          }
        }
      },
      [],
    );

    const getValidationFieldListeners = useCallback(
      (type: string) => {
        switch (type) {
          case 'required':
            return {
              onChange: ({
                value,
                fieldApi,
              }: {
                value: boolean;
                fieldApi: AnyFieldApi;
              }) => {
                handleRequiredFieldChange(
                  value,
                  MIN_PROPERTY[inputType as keyof typeof MIN_PROPERTY],
                  fieldApi,
                );
              },
            };
          case 'showTime':
            return {
              onChange: ({
                value,
                fieldApi,
              }: {
                value: boolean;
                fieldApi: AnyFieldApi;
              }) => {
                handleDateTimeChange(value, fieldApi);
              },
            };
          case 'useOptionsWithLabels':
            return {
              onChange: ({
                value,
                fieldApi,
              }: {
                value: boolean;
                fieldApi: AnyFieldApi;
              }) => {
                handleUseLabelsChange(value, fieldApi);
              },
            };
          case 'multiple':
            return {
              onChange: ({
                value,
                fieldApi,
              }: {
                value: boolean;
                fieldApi: AnyFieldApi;
              }) => {
                handleMultipleChange(value, fieldApi);
              },
            };

          default:
            return defaultFieldListeners;
        }
      },

      [
        defaultFieldListeners,
        handleDateTimeChange,
        handleMultipleChange,
        handleRequiredFieldChange,
        handleUseLabelsChange,
        inputType,
      ],
    );

    const relationTypeListeners = useMemo(
      () => ({
        onChange: ({
          value,
          fieldApi,
        }: {
          value: string;
          fieldApi: AnyFieldApi;
        }) => {
          if (fieldApi.form.getFieldValue('schema.default')) {
            const newValues = { ...fieldApi.form.state.values };
            newValues.config.validation.relationContenttype = value;
            newValues.schema.default = newValues.schema.default.filter(
              (relationValue: RelationWithoutHydration) => {
                const split = relationValue.dataUrl.split('/');
                const relationType = split[split.length - 2];
                return relationType === value;
              },
            );

            Object.keys(newValues).forEach((key) => {
              fieldApi.form.setFieldValue(key, newValues[key]);
            });
          }
          setDirty(true);
        },
      }),
      [setDirty],
    );

    const additionalCtoFieldClasses = useMemo(
      () =>
        inputType === 'select'
          ? { additionalDropdownClasses: 'top-auto! bottom-full' }
          : {},
      [inputType],
    );

    return (
      <>
        <div
          className="flex flex-col xs:flex-row items-start justify-between w-full py-6 gap-2 sm:gap-8 md:gap-14
        border-y border-slate-100 dark:border-slate-800"
        >
          <div className="flex flex-row items-center gap-2 sm:gap-8 md:gap-14 w-full">
            {Object.keys(VALIDATION).map((configName) => {
              if (
                !PROPERTIES_FIELDS[
                  inputType as keyof typeof PROPERTIES_FIELDS
                ].includes(configName)
              )
                return null;
              const name =
                VALIDATION[configName as keyof typeof VALIDATION].key;
              return (
                <form.AppField
                  key={configName}
                  name={name}
                  listeners={getValidationFieldListeners(configName)}
                >
                  {(subField) => (
                    <subField.Switch
                      label={t(
                        `ContentTypeForm.Descriptions.${VALIDATION[configName as keyof typeof VALIDATION].label}`,
                      )}
                      additionalClasses="justify-items-start"
                      labelUp
                      {...getTestProps(testId, `${configName}`, 'testId')}
                    />
                  )}
                </form.AppField>
              );
            })}
          </div>
          <div className="flex flex-row items-center justify-start sm:justify-end gap-2 sm:gap-8 md:gap-14 w-full">
            {Object.keys(ADDITIONAL).map((configName) => {
              if (
                !PROPERTIES_FIELDS[
                  inputType as keyof typeof PROPERTIES_FIELDS
                ].includes(configName) ||
                (isNestedList && ['isTitlePart'].includes(configName))
              )
                return null;
              const name =
                ADDITIONAL[configName as keyof typeof ADDITIONAL].key;
              return (
                <form.AppField
                  key={configName}
                  name={name}
                  listeners={defaultFieldListeners}
                >
                  {(subField) => (
                    <subField.Switch
                      label={t(
                        `ContentTypeForm.Descriptions.${ADDITIONAL[configName as keyof typeof ADDITIONAL].label}`,
                      )}
                      additionalClasses="justify-items-start"
                      labelUp
                      {...getTestProps(testId, `${configName}`, 'testId')}
                    />
                  )}
                </form.AppField>
              );
            })}
          </div>
        </div>
        {PROPERTIES_FIELDS[
          inputType as keyof typeof PROPERTIES_FIELDS
        ].includes('options') && (
          <form.Subscribe
            selector={({ values }) =>
              (values as ContentTypeFormProperty).config?.useOptionsWithLabels
            }
          >
            {(useOptionsWithLabels) => (
              <form.AppField
                name={
                  useOptionsWithLabels
                    ? 'config.optionsWithLabels'
                    : 'config.options'
                }
              >
                {(subField) => (
                  <subField.OptionsField
                    label={t('PropertyForm.Options')}
                    useOptionsWithLabels={useOptionsWithLabels}
                    {...getTestProps(testId, 'options', 'testId')}
                  />
                )}
              </form.AppField>
            )}
          </form.Subscribe>
        )}

        <form.AppField
          name={'config.helpText'}
          listeners={defaultFieldListeners}
        >
          {(subField) => (
            <subField.Input
              label={t('PropertyForm.HelpText')}
              {...getTestProps(testId, 'help-text', 'testId')}
            />
          )}
        </form.AppField>

        {PROPERTIES_FIELDS[
          inputType as keyof typeof PROPERTIES_FIELDS
        ].includes('blockEditorTypes') && (
          <form.AppField
            name={'config.blockEditorTypes'}
            listeners={blockEditorTypesListeners}
          >
            {(subField) => (
              <subField.Dropdown
                options={Object.keys(BLOCK_TOOLS).map((tool) => ({
                  value: tool,
                  label: t(
                    `PropertyForm.BlockTools.${BLOCK_TOOLS[tool as keyof typeof BLOCK_TOOLS]}`,
                  ),
                }))}
                label={t('PropertyForm.RestrictBlockTypes')}
                placeholder={t('PropertyForm.RestrictBlockTypes')}
                multiple
                additionalDropdownClasses="top-auto! bottom-full"
                {...getTestProps(testId, 'block-tools', 'testId')}
              />
            )}
          </form.AppField>
        )}

        {inputType === 'datasource' && !isMedia && (
          <form.AppField
            name={'config.validation.relationContenttype'}
            listeners={relationTypeListeners}
          >
            {(subField) => (
              <subField.Dropdown
                options={ctds || []}
                isDataLoading={ctdsAreLoading}
                filterCallback={filterCtds}
                label={t('PropertyForm.RestrictType')}
                ignoreNotFound
                debounceTime={300}
                extraOptions={
                  extraValidationType
                    ? [
                        {
                          label: extraValidationType.label,
                          value: extraValidationType.name,
                        },
                      ]
                    : []
                }
                {...getTestProps(testId, 'ctds', 'testId')}
              />
            )}
          </form.AppField>
        )}

        {PROPERTIES_FIELDS[
          inputType as keyof typeof PROPERTIES_FIELDS
        ].includes('default') && (
          <form.CtoCustomField
            name={'schema.default'}
            properties={
              form.getFieldValue('config') as ContentTypePropertyConfig
            }
            schema={form.getFieldValue('schema') as ContentTypePropertySchema}
            label={t('PropertyForm.Default')}
            helpText={form.getFieldValue('config.helpText') as string}
            isCtdForm
            listeners={defaultFieldListeners}
            testId={testId}
            {...additionalCtoFieldClasses}
          />
        )}
        {PROPERTIES_FIELDS[
          inputType as keyof typeof PROPERTIES_FIELDS
        ].includes('pattern') && (
          <form.AppField
            name={
              inputType === 'simpleList'
                ? 'schema.items.pattern'
                : 'schema.pattern'
            }
            listeners={defaultFieldListeners}
          >
            {(subField) => (
              <subField.Input
                label={
                  <Tooltip
                    tooltip={t('PropertyForm.PatternTooltip')}
                    tooltipPlacement="topLeft"
                    phoneTooltipPlacement="topLeft"
                    additionalClasses="w-fit inline-flex"
                  >
                    {t('PropertyForm.Pattern')}
                    <QuestionMarkCircleIcon className="text-blue w-3 ml-1" />
                  </Tooltip>
                }
                {...getTestProps(testId, 'pattern', 'testId')}
              />
            )}
          </form.AppField>
        )}
      </>
    );
  },
});

export default PropertiesSettings;
